import { useRef, useState } from "react";
import { Card, Loader, Button } from "@/ui/atoms";
import { Upload, Check, X, FileSpreadsheet, AlertCircle } from "lucide-react";
import { cn } from "@/utils/utils";
import type { TApiOTBUploadResponse } from "@/apis/proda_finance/types";

type TOTBUploadAreaProps = {
    dealId: string;
    onUpload: (dealId: string, file: File) => Promise<TApiOTBUploadResponse>;
    onSuccess: () => void;
};

export function OTBUploadArea({ dealId, onUpload, onSuccess }: TOTBUploadAreaProps) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadResult, setUploadResult] = useState<TApiOTBUploadResponse | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        setUploadError(null);
        setUploadResult(null);
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        setUploadError(null);
        setUploadResult(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        setUploading(true);
        setUploadError(null);
        setUploadResult(null);
        try {
            const result = await onUpload(dealId, selectedFile);
            if (result.success) {
                setUploadResult(result);
                setSelectedFile(null);
                onSuccess();
            } else {
                setUploadError(result.errors?.join(", ") ?? "Upload failed");
            }
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Card className="p-6 space-y-5">
            <div className="space-y-1">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Upload className="h-5 w-5 text-muted-foreground" />
                    Upload Yardi Trial Balance
                </h3>
                <p className="text-sm text-muted-foreground">
                    Upload Yardi&apos;s Trial Balance report for the deal&apos;s first month or the
                    month before, to replace the rejected or missing opening balances of this deal.
                    It is read as exported from Yardi, with no column mapping.
                </p>
            </div>

            <div
                className={cn(
                    "border-2 border-dashed rounded-lg p-8 transition-colors",
                    isDragging ? "border-primary bg-primary/5" : "border-border bg-background",
                )}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!uploading) setIsDragging(true);
                }}
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!uploading) setIsDragging(true);
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    if (uploading) return;
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleFileSelect(file);
                }}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileSelect(file);
                    }}
                    className="hidden"
                    data-testid="otb-upload-input"
                />

                {uploading ? (
                    <div className="flex flex-col items-center justify-center text-center py-6">
                        <Loader size="lg" className="mb-4" />
                        <p className="text-base font-medium">Uploading and validating...</p>
                        <p className="text-sm text-muted-foreground">{selectedFile?.name}</p>
                    </div>
                ) : selectedFile ? (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <div className="flex items-center gap-3">
                                <Check className="h-5 w-5 text-green-500" />
                                <div>
                                    <p className="font-medium text-green-700 dark:text-green-400">
                                        Yardi Trial Balance selected
                                    </p>
                                    <p className="text-sm text-green-600 dark:text-green-500">
                                        {selectedFile.name}
                                    </p>
                                </div>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={handleClearFile}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div className="flex justify-center">
                            <Button type="button" onClick={handleUpload}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Yardi Trial Balance
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-center">
                        <Upload className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-lg font-medium mb-2">
                            Drop your Yardi Trial Balance here
                        </p>
                        <p className="text-sm text-muted-foreground mb-4">
                            Drag and drop the Excel report, or select it from your computer.
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <FileSpreadsheet className="mr-2 h-4 w-4" />
                            Select File
                        </Button>
                    </div>
                )}
            </div>

            {uploadError && (
                <div
                    data-testid="otb-upload-error"
                    className="rounded-lg bg-destructive/10 p-4 text-destructive text-sm flex items-start gap-2"
                >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{uploadError}</span>
                </div>
            )}

            {uploadResult?.success && (
                <div
                    data-testid="otb-upload-success"
                    className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4 text-green-700 dark:text-green-400 text-sm"
                >
                    Yardi Trial Balance uploaded successfully - processing in the background.
                </div>
            )}
        </Card>
    );
}
